import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { gql, useMutation } from '@apollo/client';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { UploadCard } from '../../../user/ApplicationForms/CSAWForm/CSAWFormUtils';
import COVInspectionFields from './TypeSpecificFields/COVInspectionFields';
import PLTCPInspectionFields from './TypeSpecificFields/PLTCPInspectionFields';
import PLTPInspectionFields from './TypeSpecificFields/PLTPInspectionFields';
import PTPRInspectionFields from './TypeSpecificFields/PTPRInspectionFields';
import TCEBPInspectionFields from './TypeSpecificFields/TCEBPInspectionFields';

const RECORD_INSPECTION_FINDINGS = gql`
   mutation RecordInspectionFindings($id: ID!, $findings: InspectionFindingsInput!) {
      recordInspectionFindings(id: $id, findings: $findings) {
         id
         inspectionStatus
         findings {
            result
            observations
            recommendations
            attachments {
               filename
               contentType
               data
            }
            ptprFindings {
               octTctNumber
               lotNumber
               barangay
               municipality
               province
               totalLotArea
               areaDevotedToPlantation
               spacing
               treeSpeciesPlanted
               datePlanted
            }
            covFindings {
               kind
               species
               scientificName
               numberOfTrees
               quantity {
                  value
                  unit
               }
               volume {
                  cubicMeters
                  boardFeet
               }
               conveyance
               consignee
            }
            pltpFindings {
               treeInventory {
                  species
                  scientificName
                  numberOfTreesInventoried
                  grossVolume
                  netVolume
                  treeStatus
               }
               totalTrees
               totalGrossVolume
               totalNetVolume
               lotNumber
               octNumber
            }
            pltcpFindings {
               treeInventory {
                  species
                  scientificName
                  numberOfTreesInventoried
                  grossVolume
                  netVolume
                  treeCategory
               }
               totalTrees
               totalGrossVolume
               totalNetVolume
            }
            tcebpFindings {
               treeInventory {
                  species
                  numberOfTreesInventoried
                  status
                  treeCategory
                  autoSetFromInventory
                  grossVolume
                  netVolume
               }
               totalTrees
               totalGrossVolume
               totalNetVolume
            }
         }
      }
   }
`;

const UPDATE_PERMIT_STAGE = gql`
   mutation UpdatePermitStage(
      $id: ID!,
      $currentStage: String!,
      $status: String!,
      $notes: String,
      $hasInspectionReport: Boolean,
      $isInspectionResultPassed: Boolean,
      $awaitingOOP: Boolean
   ) {
      updatePermitStage(
         id: $id,
         currentStage: $currentStage,
         status: $status,
         notes: $notes,
         hasInspectionReport: $hasInspectionReport,
         isInspectionResultPassed: $isInspectionResultPassed,
         awaitingOOP: $awaitingOOP
      ) {
         id
         currentStage
         status
         hasInspectionReport
         isInspectionResultPassed
         awaitingOOP
      }
   }
`;

const TS_InspectionReportModal = ({ isOpen, onClose, inspection, application, onComplete }) => {
   // console.log('Inspection prop:', inspection);
   // console.log('Application prop:', application);

   const [findings, setFindings] = useState({
      result: '',
      observations: '',
      recommendations: '',
      attachments: [],
      ptprFindings: {
         octTctNumber: '',
         lotNumber: '',
         barangay: '',
         municipality: '',
         province: '',
         totalLotArea: 0,
         areaDevotedToPlantation: 0,
         spacing: '',
         treeSpeciesPlanted: '',
         datePlanted: ''
      },
      covFindings: {
         kind: '',
         species: '',
         scientificName: '',
         numberOfTrees: 0,
         quantity: {
            value: 0,
            unit: 'pieces'
         },
         volume: {
            cubicMeters: 0,
            boardFeet: 0
         }
      },
      pltpFindings: {
         treeInventory: [{
            species: '',
            scientificName: '',
            numberOfTreesInventoried: 0,
            grossVolume: 0,
            netVolume: 0,
            treeStatus: 'Standing'
         }],
         totalTrees: 0,
         totalGrossVolume: 0,
         totalNetVolume: 0,
         lotNumber: '',
         octNumber: ''
      },
      pltcpFindings: {
         treeInventory: [{
            species: '',
            scientificName: '',
            numberOfTreesInventoried: 0,
            grossVolume: 0,
            netVolume: 0,
            treeCategory: 'Planted'
         }],
         totalTrees: 0,
         totalGrossVolume: 0,
         totalNetVolume: 0
      },
      tcebpFindings: {
         treeInventory: [{
            species: '',
            numberOfTreesInventoried: 0,
            status: application?.requestType === 'Inventory'
               ? (application?.isRequestForCutting ? 'For Cutting' : 'For Earth-balling')
               : 'For Earth-balling',
            treeCategory: 'Natural',
            autoSetFromInventory: application?.requestType === 'Inventory'
         }],
         totalTrees: 0
      }
   });

   const [recordInspectionFindings] = useMutation(RECORD_INSPECTION_FINDINGS, {
      onError: (error) => {
         console.error('Mutation error:', error);
         toast.error(`Failed to submit report: ${error.message}`);
      }
   });

   const [updatePermitStage] = useMutation(UPDATE_PERMIT_STAGE);

   // Chrome detection for select input
   const isChrome = useMemo(() => {
      const userAgent = window.navigator.userAgent.toLowerCase();
      const isBrave = navigator.brave !== undefined;
      return userAgent.includes('chrome') && !userAgent.includes('edg') && !isBrave;
   }, []);

   const readFileAsBase64 = (file) => {
      return new Promise((resolve, reject) => {
         const reader = new FileReader();
         reader.onload = () => {
            const base64String = reader.result.split(',')[1];
            resolve(base64String);
         };
         reader.onerror = (error) => reject(error);
         reader.readAsDataURL(file);
      });
   };

   const resultOptions = [
      { value: "Pass", label: "Pass" },
      { value: "Fail", label: "Fail" },
      { value: "Needs Modification", label: "Needs Modification" }
   ];

   const handleFileChange = (e) => {
      if (e.target.files) {
         const newFiles = Array.from(e.target.files);
         setFindings(prev => ({
            ...prev,
            attachments: [...prev.attachments, ...newFiles]
         }));
      }
   };

   const removeFile = (fileToRemove) => {
      setFindings(prev => ({
         ...prev,
         attachments: prev.attachments.filter(file => file !== fileToRemove)
      }));
   };

   const handleSubmit = async () => {
      try {
         if (!findings.result) {
            toast.error('Please select an inspection result');
            return;
         }

         console.log('Inspection object:', inspection);
         console.log('Inspection ID:', inspection?._id || inspection?.id);
         console.log('Permit ID:', application?.id);

         // Validate inspection ID
         const inspectionId = inspection?._id || inspection?.id;
         if (!inspectionId) {
            toast.error('Inspection ID is missing');
            return;
         }

         // Validate permit ID
         const permitId = application?.id;
         if (!permitId) {
            toast.error('Application ID is missing');
            return;
         }

         // Process files
         const processedFiles = await Promise.all(findings.attachments.map(async (file) => {
            const base64Content = await readFileAsBase64(file);
            return {
               filename: file.name,
               contentType: file.type,
               data: base64Content
            };
         }));

         // Prepare findings input
         const findingsInput = {
            result: findings.result,
            observations: findings.observations,
            recommendations: findings.recommendations,
            attachments: processedFiles
         };

         // PTPR findings if application type is Private Tree Plantation Registration
         if (application?.applicationType === 'Private Tree Plantation Registration' && findings.ptprFindings) {
            findingsInput.ptprFindings = {
               octTctNumber: findings.ptprFindings.octTctNumber || '',
               lotNumber: findings.ptprFindings.lotNumber || '',
               barangay: findings.ptprFindings.barangay || '',
               municipality: findings.ptprFindings.municipality || '',
               province: findings.ptprFindings.province || '',
               totalLotArea: findings.ptprFindings.totalLotArea || 0,
               areaDevotedToPlantation: findings.ptprFindings.areaDevotedToPlantation || 0,
               spacing: findings.ptprFindings.spacing || '',
               treeSpeciesPlanted: findings.ptprFindings.treeSpeciesPlanted || '',
               datePlanted: findings.ptprFindings.datePlanted || ''
            };
         }

         // COV findings if application type is Certificate of Verification
         if (application?.applicationType === 'Certificate of Verification' && findings.covFindings) {
            findingsInput.covFindings = {
               kind: findings.covFindings.kind || '',
               species: findings.covFindings.species || '',
               scientificName: findings.covFindings.scientificName || '',
               numberOfTrees: findings.covFindings.numberOfTrees || 0,
               quantity: findings.covFindings.quantity || { value: 0, unit: 'pieces' },
               volume: findings.covFindings.volume || { cubicMeters: 0, boardFeet: 0 },
               conveyance: findings.covFindings.conveyance || '',
               consignee: findings.covFindings.consignee || '',
            };
         }

         // PLTP findings if application type is Private Land Timber Permit
         if (application?.applicationType === 'Private Land Timber Permit' && findings.pltpFindings) {
            findingsInput.pltpFindings = {
               treeInventory: findings.pltpFindings.treeInventory.map(tree => ({
                  species: tree.species || '',
                  scientificName: tree.scientificName || '',
                  numberOfTreesInventoried: tree.numberOfTreesInventoried || 0,
                  grossVolume: tree.grossVolume || 0,
                  netVolume: tree.netVolume || 0,
                  treeStatus: tree.treeStatus || 'Standing'
               })),
               totalTrees: findings.pltpFindings.totalTrees || 0,
               totalGrossVolume: findings.pltpFindings.totalGrossVolume || 0,
               totalNetVolume: findings.pltpFindings.totalNetVolume || 0,
               lotNumber: findings.pltpFindings.lotNumber || '',
               octNumber: findings.pltpFindings.octNumber || ''
            };
         }

         //  PLTCP findings if application type is Public Land Tree Cutting Permit
         if (application?.applicationType === 'Public Land Tree Cutting Permit' && findings.pltcpFindings) {
            findingsInput.pltcpFindings = {
               treeInventory: findings.pltcpFindings.treeInventory.map(tree => ({
                  species: tree.species || '',
                  scientificName: tree.scientificName || '',
                  numberOfTreesInventoried: tree.numberOfTreesInventoried || 0,
                  grossVolume: tree.grossVolume || 0,
                  netVolume: tree.netVolume || 0,
                  treeCategory: tree.treeCategory || 'Planted'
               })),
               totalTrees: findings.pltcpFindings.totalTrees || 0,
               totalGrossVolume: findings.pltcpFindings.totalGrossVolume || 0,
               totalNetVolume: findings.pltcpFindings.totalNetVolume || 0
            };
         }

         //  TCEBP findings if application type is Tree Cutting and/or Earth Balling Permit
         if (application?.applicationType === 'Tree Cutting and/or Earth Balling Permit' && findings.tcebpFindings) {
            const isCuttingRequest = application?.isRequestForCutting || application?.requestType === 'Cutting';

            findingsInput.tcebpFindings = {
               treeInventory: findings.tcebpFindings.treeInventory.map(tree => {
                  const baseTree = {
                     species: tree.species || '',
                     numberOfTreesInventoried: tree.numberOfTreesInventoried || 0,
                     status: tree.status || 'For Earth-balling',
                     treeCategory: tree.treeCategory || 'Natural'
                  };

                  // Add volume data for cutting requests
                  if (isCuttingRequest) {
                     baseTree.grossVolume = tree.grossVolume || 0;
                     baseTree.netVolume = tree.netVolume || 0;
                  }

                  return baseTree;
               }),
               totalTrees: findings.tcebpFindings.totalTrees || 0
            };

            // Add volume totals for cutting requests
            if (isCuttingRequest) {
               findingsInput.tcebpFindings.totalGrossVolume = findings.tcebpFindings.totalGrossVolume || 0;
               findingsInput.tcebpFindings.totalNetVolume = findings.tcebpFindings.totalNetVolume || 0;
            }
         }

         console.log('Submitting findings:', findingsInput);

         // Record inspection findings
         const { data: inspectionData } = await recordInspectionFindings({
            variables: {
               id: inspectionId,
               findings: findingsInput
            }
         });

         if (!inspectionData?.recordInspectionFindings) {
            throw new Error('Failed to record inspection findings');
         }

         // Update permit stage with different logic for COV
         const isCOV = application?.applicationType === 'Certificate of Verification';
         const updateVars = {
            id: permitId,
            currentStage: 'ForInspectionByTechnicalStaff',
            status: 'In Progress',
            notes: `Inspection completed with result: ${findings.result}`,
            hasInspectionReport: true,
            isInspectionResultPassed: findings.result === 'Pass'
         };

         // Set awaitingOOP to true only for COV applications with passing result
         if (isCOV && findings.result === 'Pass') {
            updateVars.awaitingOOP = true;
         }

         await updatePermitStage({
            variables: updateVars
         });

         toast.success('Inspection report submitted successfully');
         onComplete();
         onClose();
      } catch (error) {
         console.error('Error submitting inspection report:', error);
         toast.error(`Failed to submit report: ${error.message}`);
      }
   };

   // Result Select Component
   const ResultSelectComponent = () => {
      if (isChrome) {
         return (
            <select
               value={findings.result}
               onChange={(e) => setFindings({ ...findings, result: e.target.value })}
               className="w-full h-10 px-3 py-2 rounded-md border border-input bg-background text-sm"
            >
               <option value="" disabled>Select result</option>
               {resultOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                     {option.label}
                  </option>
               ))}
            </select>
         );
      }

      return (
         <Select
            value={findings.result}
            onValueChange={(value) => setFindings({ ...findings, result: value })}
         >
            <SelectTrigger>
               <SelectValue placeholder="Select result" />
            </SelectTrigger>
            <SelectContent>
               {resultOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                     {option.label}
                  </SelectItem>
               ))}
            </SelectContent>
         </Select>
      );
   };

   useEffect(() => {
      if (application?.applicationType === 'Tree Cutting and/or Earth Balling Permit') {
         console.log('Application data in modal:', {
            requestType: application.requestType,
            isRequestForCutting: application.isRequestForCutting,
            isRequestForEarthBalling: application.isRequestForEarthBalling
         });
      }
   }, [application]);

   return (
      <Dialog open={isOpen} onOpenChange={onClose}>
         <DialogContent className="max-w-6xl max-h-[90vh] flex flex-col">
            <DialogHeader>
               <DialogTitle>Submit Inspection Report</DialogTitle>
               <DialogDescription>
                  Record inspection findings and submit report for {application?.applicationNumber}
               </DialogDescription>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto pr-2 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full">
               <div className="space-y-6 py-4">
                  <div className="space-y-2">
                     <Label>Inspection Result <span className="text-red-500">*</span></Label>
                     <ResultSelectComponent />
                  </div>

                  {application?.applicationType === 'Certificate of Verification' && (
                     <COVInspectionFields
                        findings={findings}
                        setFindings={setFindings}
                     />
                  )}

                  {application?.applicationType === 'Private Land Timber Permit' && (
                     <PLTPInspectionFields
                        findings={findings}
                        setFindings={setFindings}
                     />
                  )}

                  {application?.applicationType === 'Public Land Tree Cutting Permit' && (
                     <PLTCPInspectionFields
                        findings={findings}
                        setFindings={setFindings}
                     />
                  )}

                  {application?.applicationType === 'Tree Cutting and/or Earth Balling Permit' && (
                     <TCEBPInspectionFields
                        findings={findings}
                        setFindings={setFindings}
                        application={application}
                     />
                  )}

                  {application?.applicationType === 'Private Tree Plantation Registration' && (
                     <PTPRInspectionFields
                        findings={findings}
                        setFindings={setFindings}
                     />
                  )}

                  <div className="space-y-2">
                     <Label>Observations</Label>
                     <Textarea
                        value={findings.observations}
                        onChange={(e) => setFindings({ ...findings, observations: e.target.value })}
                        placeholder="Enter detailed observations (optional)..."
                        rows={4}
                     />
                  </div>

                  <div className="space-y-2">
                     <Label>Recommendations</Label>
                     <Textarea
                        value={findings.recommendations}
                        onChange={(e) => setFindings({ ...findings, recommendations: e.target.value })}
                        placeholder="Enter recommendations (optional)..."
                        rows={3}
                     />
                  </div>

                  <UploadCard
                     label="Supporting Documents & Photos"
                     documentLabel="Upload inspection photos and documents (PDF, DOC, XLS)"
                     files={findings.attachments}
                     onFileChange={handleFileChange}
                     onRemoveFile={removeFile}
                     accept=".pdf,.doc,.docx,.xls,.xlsx,image/*"
                  />
               </div>
            </div>

            <div className="flex justify-end space-x-2 pt-4 border-t">
               <Button variant="outline" onClick={onClose}>
                  Cancel
               </Button>
               <Button
                  onClick={handleSubmit}
                  disabled={!findings.result}
                  className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white"
               >
                  Submit Report
               </Button>
            </div>
         </DialogContent>
      </Dialog>
   );
};

export default TS_InspectionReportModal;
